import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { format, parseISO } from 'date-fns';

const SuspiciousActivityTable = ({ suspiciousActivity }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    // Severity level classification
    const getSeverityLevel = (eventType) => {
        const severity = {
            'Critical': ['Attack Detected', 'Account Compromise', 'Brute Force Attack', 'Breached Password'],
            'High': ['Suspicious IP Address', 'Account Locked', 'Malicious Activity', 'Intrusion Attempt'],
            'Medium': ['Failed Password Attempt', 'Rate Limit Exceeded', 'Security Warning', 'Suspicious Activity'],
            'Low': ['MFA Event', 'Password Changed', 'User Deleted', 'Anomaly Detected']
        };
        
        for (const [level, types] of Object.entries(severity)) {
            if (types.includes(eventType)) return level;
        }
        return 'Low';
    };

    // Event categories
    const getEventCategory = (eventType) => {
        const categories = {
            'authentication': ['Failed Password Attempt', 'Failed User Attempt (User Not Found)', 'MFA Event'],
            'security': ['Suspicious IP Address', 'Brute Force Attack', 'Account Locked', 'Rate Limit Exceeded'],
            'account': ['Password Changed', 'User Deleted', 'Account Compromise'],
            'threats': ['Attack Detected', 'Malicious Activity', 'Fraudulent Activity', 'Intrusion Attempt'],
            'warnings': ['Security Warning', 'Suspicious Activity', 'Anomaly Detected']
        };
        
        for (const [category, types] of Object.entries(categories)) {
            if (types.includes(eventType)) return category;
        }
        return 'other';
    };

    // Filter data based on search term, type, category, and severity
    const filteredData = suspiciousActivity.filter(activity => {
        const matchesSearch = 
            activity.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            activity.ip?.includes(searchTerm) ||
            activity.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            activity.connection?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = filterType === 'all' || activity.type === filterType;
        const matchesCategory = filterCategory === 'all' || getEventCategory(activity.type) === filterCategory;
        const matchesSeverity = filterSeverity === 'all' || getSeverityLevel(activity.type) === filterSeverity;
        
        return matchesSearch && matchesType && matchesCategory && matchesSeverity;
    });

    // Get unique types for filter dropdown
    const uniqueTypes = [...new Set(suspiciousActivity.map(activity => activity.type))].filter(Boolean);

    // Event type styling with enhanced types
    const getEventTypeStyle = (eventType) => {
        const styles = {
            'Failed Password Attempt': { color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/20', icon: 'mdi:lock-alert' },
            'Failed User Attempt (User Not Found)': { color: 'text-orange-600', bg: 'bg-orange-200 dark:bg-orange-900/30', icon: 'mdi:account-alert' },
            'Suspicious IP Address': { color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/20', icon: 'mdi:earth-alert' },
            'MFA Event': { color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/20', icon: 'mdi:two-factor-authentication' },
            'Account Locked': { color: 'text-red-600', bg: 'bg-red-200 dark:bg-red-900/30', icon: 'mdi:account-lock' },
            'Brute Force Attack': { color: 'text-red-700', bg: 'bg-red-300 dark:bg-red-900/40', icon: 'mdi:flash' },
            'Password Changed': { color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/20', icon: 'mdi:key' },
            'User Deleted': { color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/20', icon: 'mdi:account-remove' },
            'Rate Limit Exceeded': { color: 'text-yellow-600', bg: 'bg-yellow-100 dark:bg-yellow-900/20', icon: 'mdi:timer-alert' },
            'Security Warning': { color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/20', icon: 'mdi:alert' },
            'Breached Password': { color: 'text-red-800', bg: 'bg-red-400 dark:bg-red-900/50', icon: 'mdi:shield-alert' },
            'Attack Detected': { color: 'text-red-900', bg: 'bg-red-500 dark:bg-red-900/60', icon: 'mdi:sword' },
            'Malicious Activity': { color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/20', icon: 'mdi:bug' },
            'Fraudulent Activity': { color: 'text-pink-600', bg: 'bg-pink-100 dark:bg-pink-900/20', icon: 'mdi:credit-card-off' },
            'Account Compromise': { color: 'text-red-800', bg: 'bg-red-300 dark:bg-red-900/40', icon: 'mdi:account-alert' },
            'Unauthorized Access': { color: 'text-orange-600', bg: 'bg-orange-200 dark:bg-orange-900/30', icon: 'mdi:door-open' },
            'Intrusion Attempt': { color: 'text-red-700', bg: 'bg-red-200 dark:bg-red-900/30', icon: 'mdi:home-alert' },
            'Suspicious Activity': { color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/20', icon: 'mdi:eye-alert' },
            'Anomaly Detected': { color: 'text-blue-600', bg: 'bg-blue-200 dark:bg-blue-900/30', icon: 'mdi:chart-line-variant' },
            'Blocked Attempt': { color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/20', icon: 'mdi:block-helper' },
            'Security Event': { color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/20', icon: 'mdi:shield' }
        };
        return styles[eventType] || { color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/20', icon: 'mdi:alert-circle' };
    };

    // Calculate statistics
    const eventStats = useMemo(() => {
        const stats = {};
        const severityStats = {};
        const categoryStats = {};
        
        suspiciousActivity.forEach(event => {
            stats[event.type] = (stats[event.type] || 0) + 1;
            const severity = getSeverityLevel(event.type);
            severityStats[severity] = (severityStats[severity] || 0) + 1;
            const category = getEventCategory(event.type);
            categoryStats[category] = (categoryStats[category] || 0) + 1;
        });
        
        return { byType: stats, bySeverity: severityStats, byCategory: categoryStats };
    }, [suspiciousActivity]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

    // Reset to page 1 when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterType, filterCategory, filterSeverity]);

    return (
        <div className="rounded-md flex flex-col gap-3">
            <div className="flex items-center gap-1">
                <Icon icon="mdi:alert-circle" className="text-xl text-yellow-500" />
                <h4 className="font-semibold text-font-primary">Suspicious Activity</h4>
            </div>

            {/* Search and Filter Controls */}
            <div className="flex flex-col gap-3">
                {/* Search Bar */}
                <div>
                    <input
                        type="text"
                        placeholder="Search by type, description, IP, user ID, or connection..."
                        className="w-full px-3 py-2 input-textbox rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                {/* Filter Controls */}
                <div className="flex flex-row flex-wrap gap-3 w-full">
                    <div className="w-full">
                        <label className="block text-xs text-font-secondary mb-1">Event Type</label>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full px-3 py-2 dropdown-container rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                            <option value="all">All Types</option>
                            {uniqueTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="w-full">
                        <label className="block text-xs text-font-secondary mb-1">Category</label>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="w-full px-3 py-2 dropdown-container rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                            <option value="all">All Categories</option>
                            <option value="authentication">Authentication</option>
                            <option value="security">Security</option>
                            <option value="account">Account</option>
                            <option value="threats">Threats</option>
                            <option value="warnings">Warnings</option>
                        </select>
                    </div>
                    
                    <div className="w-full">
                        <label className="block text-xs text-font-secondary mb-1">Severity</label>
                        <select
                            value={filterSeverity}
                            onChange={(e) => setFilterSeverity(e.target.value)}
                            className="w-full px-3 py-2 dropdown-container rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                            <option value="all">All Severities</option>
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            {filteredData.length === 0 ? (
                <div className="text-center py-12 flex flex-row gap-2 items-center justify-center">
                    <Icon icon="mdi:shield-check" className="text-3xl text-font-secondary" />
                    <p className="text-font-secondary text-lg font-semibold">
                        {suspiciousActivity.length === 0 
                            ? 'No suspicious activity detected' 
                            : 'No activities match your search criteria'
                        }
                    </p>
                </div>
            ) : (
                <div className="card-container overflow-x-auto p-0">
                    <table className="min-w-full divide-y divide-bgAccent">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Type & Severity
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Description
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    IP Address
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    User ID
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Connection
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-bgAccent">
                            {paginatedData.map((activity, index) => {
                                const eventStyle = getEventTypeStyle(activity.type);
                                const severity = getSeverityLevel(activity.type);
                                const severityColor = {
                                    'Critical': 'text-red-600',
                                    'High': 'text-orange-500',
                                    'Medium': 'text-yellow-500',
                                    'Low': 'text-green-500'
                                }[severity] || 'text-gray-500';
                                
                                return (
                                    <tr key={index} className="hover:bg-bgAccent transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-font-primary">
                                            {activity.date ? format(parseISO(activity.date), 'MMM d, yyyy HH:mm') : 'N/A'}
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <Icon 
                                                        icon={eventStyle.icon} 
                                                        className={`text-sm ${eventStyle.color}`} 
                                                    />
                                                    <span className={`${eventStyle.color} font-medium`}>
                                                        {activity.type || 'Unknown'}
                                                    </span>
                                                </div>
                                                <div className={`text-xs ${severityColor} font-medium`}>
                                                    {severity}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-sm text-font-secondary max-w-xs">
                                            <div className="truncate" title={activity.description}>
                                                {activity.description || 'No description available'}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm text-font-primary font-mono">
                                            {activity.ip || 'N/A'}
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm text-font-primary font-mono">
                                            {activity.userId || 'N/A'}
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm text-font-secondary">
                                            {activity.connection || 'N/A'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination Controls */}
            {filteredData.length > 0 && (
                <div className="flex items-center justify-between px-5 pt-3 border-t border-bgAccent text-sm">
                    <div className="hidden sm:block">
                        <span className="text-font-secondary">
                            Showing <span className="font-semibold text-primary">{Math.min(startIndex + 1, filteredData.length)}</span> to <span className="font-semibold text-primary">{Math.min(startIndex + rowsPerPage, filteredData.length)}</span> of <span className="font-semibold text-primary">{filteredData.length}</span> results
                        </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-4 w-full sm:w-auto justify-between sm:justify-end">
                        <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="icon-park-outline:double-left" className="text-lg sm:text-xl text-accent" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="material-symbols:arrow-back-ios-new" className="text-base sm:text-lg text-accent" />
                        </button>
                        <span className="text-font-secondary text-xs sm:text-sm">
                            <span className="font-bold text-accent">{currentPage}</span>/<span className="font-bold">{totalPages}</span>
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="material-symbols:arrow-forward-ios" className="text-base sm:text-lg text-accent" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="icon-park-outline:double-right" className="text-lg sm:text-xl text-accent" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuspiciousActivityTable;
